import React from 'react';
import { Alert, Card, Empty, Space, Spin, Tag, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { ISlotLayoutPlacement, useGetSlotLayoutQuery } from '../../redux/api/inventory';
import { getMaterialPreviewColor } from '../Dimensions/MaterialShapePreview';
import { HEX_BAR, ROUND_BAR, ROUND_TUBE } from '../../config/constants';

const { Text } = Typography;

interface SlotFrontViewProps {
	/** Inventory location id — must be an INDIVIDUAL_SLOTS location. */
	locationId: string;
}

type PlacedSlotLayoutPlacement = ISlotLayoutPlacement & {
	x: number;
	y: number;
	w: number;
	h: number;
};

/** A placement the packer actually placed (has a footprint), vs. one it couldn't fit. */
const isPlaced = (
	placement: ISlotLayoutPlacement,
): placement is PlacedSlotLayoutPlacement =>
	placement.x != null &&
	placement.y != null &&
	placement.w != null &&
	placement.h != null;

/** Adapts a flat slot-layout placement into the record shape the color helper expects. */
const toPreviewRecord = (placement: PlacedSlotLayoutPlacement) => ({
	material_shape: { display_name: placement.shape },
	material_type: { display_name: placement.material_type },
	width: placement.w,
	height: placement.h,
	outer_diameter: placement.w,
});

/**
 * The bar's cross-section drawn TRUE TO SCALE, filling its w x h footprint —
 * real slot-inch geometry, not the material list's fixed 100x44 thumbnail
 * (scaling that canvas onto the footprint distorted the shape, since x and y
 * scaled by different factors). Round/hex are inscribed in the footprint;
 * everything else IS its rectangular footprint.
 */
const FootprintShape = ({
	placement,
	fill,
	strokeWidth,
}: {
	placement: PlacedSlotLayoutPlacement;
	fill: string;
	strokeWidth: number;
}) => {
	const { w, h, shape } = placement;
	const common = { fill, stroke: '#3f3f3f', strokeWidth };
	if (shape === ROUND_BAR || shape === ROUND_TUBE) {
		return <ellipse cx={w / 2} cy={h / 2} rx={w / 2} ry={h / 2} {...common} />;
	}
	if (shape === HEX_BAR) {
		const inset = w * 0.25;
		const points = `${inset},0 ${w - inset},0 ${w},${h / 2} ${w - inset},${h} ${inset},${h} 0,${h / 2}`;
		return <polygon points={points} {...common} />;
	}
	return <rect width={w} height={h} rx={Math.min(w, h) * 0.05} {...common} />;
};

/**
 * Read-only "front view" (end-on) diagram of an INDIVIDUAL_SLOTS location:
 * draws each material length's 2D footprint to scale inside the slot outline,
 * plus a compact occupancy readout. Purely informational — the backend packer
 * (not this component) decides what fits; `occupied.weight` and `fits_more`
 * are read-outs, not limits enforced here.
 */
const SlotFrontView: React.FC<SlotFrontViewProps> = ({ locationId }) => {
	const { data, isFetching, isError } = useGetSlotLayoutQuery(locationId, {
		skip: !locationId,
	});

	if (isFetching) {
		return (
			<Card size="small" style={{ marginBottom: 16, textAlign: 'center' }}>
				<Spin />
			</Card>
		);
	}

	if (isError || !data) {
		return (
			<Card size="small" style={{ marginBottom: 16 }}>
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description="Slot front view is not available for this location"
				/>
			</Card>
		);
	}

	const { slot, placements, occupied, fits_more, all_placed } = data;
	const placed = placements.filter(isPlaced);
	const unplaced = placements.filter((placement) => !isPlaced(placement));

	const viewWidth = slot.max_width > 0 ? slot.max_width : 1;
	const viewHeight = slot.max_height > 0 ? slot.max_height : 1;
	const clearance = Number(slot.clearance) || 0;
	const outlineStroke = Math.max(viewWidth, viewHeight) * 0.012;
	const placementStroke = Math.max(viewWidth, viewHeight) * 0.006;

	// Largest id-label size that fits inside a bar's footprint (bounded by both
	// its height and the label's width, with 10% side padding so adjacent bars'
	// labels never touch). Below MIN_LABEL_IN the id is illegible, so we drop
	// the on-bar text and rely on the hover tooltip (<title>) instead.
	const MIN_LABEL_IN = 0.14;
	const labelFontSize = (p: PlacedSlotLayoutPlacement) =>
		Math.min(
			p.h * 0.42,
			(p.w * 0.9) / Math.max(p.friendly_id.length * 0.6, 1),
			0.5
		);

	return (
		<Card
			size="small"
			title={`Slot ${slot.code} — Front View`}
			style={{ marginBottom: 16 }}
		>
			<div
				style={{
					display: 'flex',
					gap: 24,
					flexWrap: 'wrap',
					alignItems: 'flex-start',
				}}
			>
				<svg
					viewBox={`0 0 ${viewWidth} ${viewHeight}`}
					role="img"
					aria-label={`Slot ${slot.code} front view`}
					style={{
						width: '100%',
						maxWidth: 320,
						height: 'auto',
						aspectRatio: `${viewWidth} / ${viewHeight}`,
						background: '#fafafa',
						border: '1px solid #d9d9d9',
					}}
				>
					<defs>
						<pattern
							id="clearance-hatch"
							width={0.16}
							height={0.16}
							patternUnits="userSpaceOnUse"
							patternTransform="rotate(45)"
						>
							<line x1={0} y1={0} x2={0} y2={0.16} stroke="#d9d9d9" strokeWidth={0.05} />
						</pattern>
					</defs>
					<rect
						data-testid="slot-outline"
						x={0}
						y={0}
						width={viewWidth}
						height={viewHeight}
						fill="#ffffff"
						stroke="#8c8c8c"
						strokeWidth={outlineStroke}
					/>
					{/* The shared maneuvering clearance (one margin per axis: usable =
					    max − clearance). Bars pack from the bottom-left, so the reserved
					    band sits along the top and right edges — hatched so it reads as
					    "not packable", matching exactly what the packer excludes. */}
					{clearance > 0 && (
						<g data-testid="clearance-band">
							<rect
								x={0}
								y={0}
								width={viewWidth}
								height={Math.min(clearance, viewHeight)}
								fill="url(#clearance-hatch)"
								stroke="none"
							/>
							<rect
								x={Math.max(viewWidth - clearance, 0)}
								y={Math.min(clearance, viewHeight)}
								width={Math.min(clearance, viewWidth)}
								height={Math.max(viewHeight - clearance, 0)}
								fill="url(#clearance-hatch)"
								stroke="none"
							/>
						</g>
					)}
					{placed.map((placement) => (
						<g
							key={placement.material_length_id}
							data-testid="slot-placement"
							data-friendly-id={placement.friendly_id}
							transform={`translate(${placement.x}, ${
								viewHeight - placement.y - placement.h
							})`}
						>
							<title>{`${placement.friendly_id} (${placement.shape})`}</title>
							{/* Reserved packing cell — subtle dashed outline. Visible only
							    where the cross-section doesn't fill it (round/hex corners). */}
							<rect
								x={0}
								y={0}
								width={placement.w}
								height={placement.h}
								fill="none"
								stroke="#bfbfbf"
								strokeWidth={placementStroke}
								strokeDasharray={`${placementStroke * 3} ${placementStroke * 3}`}
							/>
							{/* The material's cross-section, drawn true to scale. */}
							<FootprintShape
								placement={placement}
								fill={getMaterialPreviewColor(toPreviewRecord(placement))}
								strokeWidth={placementStroke}
							/>
							{/* The bar's length id — only when it fits legibly; smaller
							    bars rely on the hover tooltip above instead. */}
							{labelFontSize(placement) >= MIN_LABEL_IN && (
								<text
									x={placement.w / 2}
									y={placement.h / 2}
									textAnchor="middle"
									dominantBaseline="central"
									fontSize={labelFontSize(placement)}
									fill="#141414"
									stroke="#ffffff"
									strokeWidth={labelFontSize(placement) * 0.12}
									style={{
										pointerEvents: 'none',
										fontWeight: 600,
										paintOrder: 'stroke',
									}}
								>
									{placement.friendly_id}
								</text>
							)}
						</g>
					))}
				</svg>

				<Space direction="vertical" size={6} style={{ minWidth: 220 }}>
					<Text>
						<strong>{occupied.bars}</strong> bar{occupied.bars === 1 ? '' : 's'}{' '}
						in this slot
					</Text>
					<Text type="secondary">
						{Number(occupied.weight).toFixed(1)} lb total (informational only —
						not a capacity limit)
					</Text>
					{clearance > 0 && (
						<Text type="secondary">
							Hatched band = {clearance}&quot; maneuvering clearance (reserved,
							not packable)
						</Text>
					)}

					{fits_more.length > 0 && (
						<Space direction="vertical" size={2} wrap>
							{fits_more.map((entry) => (
								<Tag key={`${entry.w}x${entry.h}`} color="blue">
									Fits {entry.count} more of {entry.w}×{entry.h} in.
								</Tag>
							))}
						</Space>
					)}

					{!all_placed && (
						<Alert
							type="warning"
							showIcon
							icon={<WarningOutlined />}
							message="Slot is over-packed"
							description="Some material lengths don't fit and aren't shown to scale."
						/>
					)}

					{unplaced.length > 0 && (
						<Alert
							type="warning"
							showIcon
							message={`${unplaced.length} unplaced`}
							description={unplaced
								.map((placement) => placement.friendly_id)
								.join(', ')}
						/>
					)}
				</Space>
			</div>
		</Card>
	);
};

export default SlotFrontView;
